/**
 * Video Progress Tracking API Service
 * This service handles communication with the Azure Functions backend for video progress tracking.
 */

import axios from 'axios';

class VideoProgressService {
  constructor() {
    this.baseURL = process.env.REACT_APP_API_BASE;
    console.log('VideoProgressService initialized with baseURL:', this.baseURL);
  }

  /**
   * Get video progress for a user
   * @param {string} userId - User ID
   * @param {string} videoId - Video ID (optional, if not provided returns all progress for user)
   * @returns {Promise} - API response with progress data
   */
  async getVideoProgress(userId, videoId = null) {
    try {
      let url = `${this.baseURL}/video-progress/get?userId=${userId}`;
      if (videoId) {
        url += `&videoId=${videoId}`;
      }
      
      console.log(`Getting video progress from: ${url}`);
      
      const response = await axios.get(url, {
        headers: { 'x-user-id': userId }
      });
      
      // Handle new Firebase response format
      if (response.data && response.data.data) {
        return response.data;
      }
      
      // Fallback for old format
      return { data: response.data, message: "Progress retrieved successfully" };
    } catch (error) {
      console.error('Error fetching video progress:', error);
      throw error;
    }
  }

  /**
   * Update video progress
   * @param {Object} progressData - Progress data object
   * @returns {Promise} - API response
   */
  async updateVideoProgress(progressData) {
    try {
      console.log('Updating video progress:', progressData);
      
      const response = await axios.post(`${this.baseURL}/video-progress/post`, progressData);
      
      // Handle new Firebase response format
      if (response.data && response.data.data) {
        return response.data;
      }
      
      // Fallback for old format
      return { data: response.data, message: "Progress updated successfully" };
    } catch (error) {
      console.error('Error updating video progress:', error);
      throw error;
    }
  }

  /**
   * Calculate overall progress for a user
   * @param {string} userId - User ID
   * @returns {Promise} - Overall progress statistics
   */
  async getOverallProgress(userId) {
    try {
      const progressResponse = await this.getVideoProgress(userId);
      const progressData = progressResponse.data || [];
      
      const totalVideos = progressData.length;
      const completedVideos = progressData.filter(p => p.completed).length;
      const averageProgress = totalVideos > 0 
        ? progressData.reduce((sum, p) => sum + (p.progress || 0), 0) / totalVideos 
        : 0;
      
      return {
        totalVideos,
        completedVideos,
        averageProgress: Math.round(averageProgress),
        inProgressVideos: totalVideos - completedVideos,
        progressData
      };
    } catch (error) {
      console.error('Error calculating overall progress:', error);
      throw error;
    }
  }

  /**
   * Get progress by category
   * @param {string} userId - User ID
   * @param {Array} videos - All videos data for category mapping
   * @returns {Promise} - Progress grouped by category
   */
  async getProgressByCategory(userId, videos = []) {
    try {
      const progressResponse = await this.getVideoProgress(userId);
      const progressData = progressResponse.data || [];
      
      const categoryProgress = {};
      
      progressData.forEach(progress => {
        const video = videos.find(v => v.id === progress.videoId);
        const category = video?.category || 'Uncategorized';
        
        if (!categoryProgress[category]) {
          categoryProgress[category] = {
            totalVideos: 0,
            completedVideos: 0,
            totalProgress: 0,
            averageProgress: 0
          };
        }
        
        categoryProgress[category].totalVideos++;
        categoryProgress[category].totalProgress += progress.progress || 0;
        
        if (progress.completed) {
          categoryProgress[category].completedVideos++;
        }
      });
      
      // Calculate averages
      Object.keys(categoryProgress).forEach(category => {
        const cat = categoryProgress[category];
        cat.averageProgress = cat.totalVideos > 0 
          ? Math.round(cat.totalProgress / cat.totalVideos) 
          : 0;
      });
      
      return categoryProgress;
    } catch (error) {
      console.error('Error calculating category progress:', error);
      throw error;
    }
  }

  /**
   * Mark video as completed
   * @param {string} userId - User ID
   * @param {string} videoId - Video ID
   * @param {number} duration - Video duration in seconds
   * @returns {Promise} - API response
   */
  async markVideoCompleted(userId, videoId, duration = 0) {
    return this.updateVideoProgress({
      userId,
      videoId,
      progress: 100,
      currentTime: duration,
      duration,
      completed: true
    });
  }

  /**
   * Check if video was posted recently (within 30 days)
   * @param {string|Date} createdAt - Video creation date
   * @returns {boolean} - True if video is recent
   */
  isRecentVideo(createdAt) {
    if (!createdAt) return false;
    const now = new Date();
    const videoDate = new Date(createdAt);
    const daysDiff = Math.floor((now - videoDate) / (1000 * 60 * 60 * 24));
    return daysDiff <= 30;
  }

  /**
   * Format watch time into readable format
   * @param {number} seconds - Time in seconds
   * @returns {string} - Formatted time string
   */
  formatWatchTime(seconds) {
    if (seconds < 60) {
      return `${seconds}s`;
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      return `${minutes}m`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return `${hours}h ${minutes}m`;
    }
  }

  /**
   * Get overall progress for a user
   * @param {string} userId - User ID
   * @returns {Promise} - Overall progress data
   */
  async getUserOverallProgress(userId) {
    try {
      const progressResponse = await this.getVideoProgress(userId);
      const progressData = progressResponse.data || [];
      
      const totalVideos = progressData.length;
      const completedVideos = progressData.filter(p => p.completed).length;
      const inProgress = progressData.filter(p => p.progress > 0 && !p.completed).length;
      const completionRate = totalVideos > 0 ? (completedVideos / totalVideos) * 100 : 0;
      const averageProgress = totalVideos > 0 
        ? progressData.reduce((sum, p) => sum + (p.progress || 0), 0) / totalVideos 
        : 0;
      
      // Calculate total watch time
      const totalWatchTimeSeconds = progressData.reduce((sum, p) => sum + (p.currentTime || 0), 0);
      const hours = Math.floor(totalWatchTimeSeconds / 3600);
      const minutes = Math.floor((totalWatchTimeSeconds % 3600) / 60);
      const seconds = totalWatchTimeSeconds % 60;
      const totalWatchTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

      // Progress by category (simplified)
      const progressByCategory = {};
      progressData.forEach(progress => {
        const category = progress.category || 'General';
        if (!progressByCategory[category]) {
          progressByCategory[category] = {
            total: 0,
            completed: 0,
            averageProgress: 0
          };
        }
        progressByCategory[category].total++;
        if (progress.completed) {
          progressByCategory[category].completed++;
        }
        progressByCategory[category].averageProgress += progress.progress || 0;
      });

      // Calculate averages for categories
      Object.keys(progressByCategory).forEach(category => {
        const cat = progressByCategory[category];
        cat.averageProgress = cat.total > 0 ? cat.averageProgress / cat.total : 0;
      });

      return {
        totalVideos,
        completedVideos,
        inProgress,
        completionRate,
        averageProgress,
        totalWatchTime,
        progressByCategory
      };
    } catch (error) {
      console.error('Error getting user overall progress:', error);
      return {
        totalVideos: 0,
        completedVideos: 0,
        inProgress: 0,
        completionRate: 0,
        averageProgress: 0,
        totalWatchTime: '00:00:00',
        progressByCategory: {}
      };
    }
  }

  /**
   * Calculate overall progress statistics
   * @param {Array} progressData - Array of progress data
   * @param {number} totalVideos - Total number of videos
   * @returns {Object} - Overall progress statistics
   */
  calculateOverallProgress(progressData, totalVideos) {
    if (!progressData || progressData.length === 0) {
      return {
        totalVideos: totalVideos || 0,
        watchedVideos: 0,
        completedVideos: 0,
        averageProgress: 0,
        totalWatchTime: 0
      };
    }

    const watchedVideos = progressData.filter(p => p.progress > 0).length;
    const completedVideos = progressData.filter(p => p.completed).length;
    const totalProgress = progressData.reduce((sum, p) => sum + (p.progress || 0), 0);
    const averageProgress = totalVideos > 0 ? Math.round(totalProgress / totalVideos) : 0;
    const totalWatchTime = progressData.reduce((sum, p) => sum + (p.currentTime || 0), 0);

    return {
      totalVideos: totalVideos || 0,
      watchedVideos,
      completedVideos,
      averageProgress,
      totalWatchTime: Math.round(totalWatchTime)
    };
  }
}

// Export a singleton instance
const videoProgressService = new VideoProgressService();
export default videoProgressService;
